/**
 * Hooks TanStack Query — TEXTPRINT Sprint 2 (Devis + Lignes + Sous-traitants).
 * Brief SIM-PLATFORM-TEXTIPRINT-2026-001 §C + §H.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  textprintDevisApi,
  textprintLignesDevisApi,
  textprintSousTraitantsApi,
  type DevisListParams, type CreateDevisPayload, type UpdateDevisPayload, type ConvertirDevisPayload,
  type CreateLigneDevisPayload, type UpdateLigneDevisPayload,
  type SousTraitantListParams, type CreateSousTraitantPayload, type UpdateSousTraitantPayload,
} from '@/api/textprint-sprint2';

const K = {
  devis:         ['textprint-sprint2', 'devis']         as const,
  lignes:        ['textprint-sprint2', 'lignes-devis']  as const,
  sousTraitants: ['textprint-sprint2', 'sous-traitants'] as const,
  // Caches externes invalidés lors de la conversion devis → commande
  // (doit matcher COMMANDES_KEY dans useTextprint.ts)
  commandes:     ['operations', 'textprint', 'commandes'] as const,
};

// ── DEVIS ───────────────────────────────────────────────────────────────
export const useDevisImpression = (params: DevisListParams = {}) =>
  useQuery({
    queryKey:  [...K.devis, params],
    queryFn:   () => textprintDevisApi.list(params),
    staleTime: 30_000,
  });

export const useDevisImpressionDetail = (id: string | undefined) =>
  useQuery({
    queryKey:  [...K.devis, id],
    queryFn:   () => textprintDevisApi.get(id!),
    enabled:   !!id,
    staleTime: 15_000,
  });

export const useCreateDevisImpression = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateDevisPayload) => textprintDevisApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.devis }),
  });
};

export const useUpdateDevisImpression = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDevisPayload }) =>
      textprintDevisApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: K.devis }),
  });
};

export const useDeleteDevisImpression = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => textprintDevisApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.devis }),
  });
};

// ── Workflow devis ──────────────────────────────────────────────────────
export const useEnvoyerDevis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => textprintDevisApi.envoyer(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.devis }),
  });
};

export const useAccepterDevis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => textprintDevisApi.accepter(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.devis }),
  });
};

export const useRefuserDevis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => textprintDevisApi.refuser(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.devis }),
  });
};

export const useConvertirDevis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: ConvertirDevisPayload }) =>
      textprintDevisApi.convertir(id, payload ?? {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: K.devis });
      qc.invalidateQueries({ queryKey: K.commandes });
    },
  });
};

// ── LIGNES DEVIS ────────────────────────────────────────────────────────
export const useLignesDevis = (devisId: string | undefined) =>
  useQuery({
    queryKey:  [...K.lignes, devisId],
    queryFn:   () => textprintLignesDevisApi.list(devisId!),
    enabled:   !!devisId,
    staleTime: 15_000,
  });

export const useCreateLigneDevis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ devisId, payload }: { devisId: string; payload: CreateLigneDevisPayload }) =>
      textprintLignesDevisApi.create(devisId, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [...K.lignes, vars.devisId] });
      // recalcul des totaux côté backend → invalider le devis (détail + liste)
      qc.invalidateQueries({ queryKey: K.devis });
    },
  });
};

export const useUpdateLigneDevis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ devisId, lineId, payload }: { devisId: string; lineId: string; payload: UpdateLigneDevisPayload }) =>
      textprintLignesDevisApi.update(devisId, lineId, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [...K.lignes, vars.devisId] });
      qc.invalidateQueries({ queryKey: K.devis });
    },
  });
};

export const useDeleteLigneDevis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ devisId, lineId }: { devisId: string; lineId: string }) =>
      textprintLignesDevisApi.remove(devisId, lineId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [...K.lignes, vars.devisId] });
      qc.invalidateQueries({ queryKey: K.devis });
    },
  });
};

// ── SOUS-TRAITANTS ──────────────────────────────────────────────────────
export const useSousTraitants = (params: SousTraitantListParams = {}) =>
  useQuery({
    queryKey:  [...K.sousTraitants, params],
    queryFn:   () => textprintSousTraitantsApi.list(params),
    staleTime: 60_000,
  });

export const useSousTraitant = (id: string | undefined) =>
  useQuery({
    queryKey:  [...K.sousTraitants, id],
    queryFn:   () => textprintSousTraitantsApi.get(id!),
    enabled:   !!id,
    staleTime: 30_000,
  });

export const useCreateSousTraitant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateSousTraitantPayload) => textprintSousTraitantsApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.sousTraitants }),
  });
};

export const useUpdateSousTraitant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateSousTraitantPayload }) =>
      textprintSousTraitantsApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: K.sousTraitants }),
  });
};

export const useDeleteSousTraitant = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => textprintSousTraitantsApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: K.sousTraitants }),
  });
};
